import { ref } from 'vue';
import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import api from '@/common/utils/api';
import {
  ClusterRegistration,
  ClusterRegistrationConfigShowResponse,
  ClusterRegistrationConfigUpdate,
  TaskState,
  BackendTask,
} from '@schema';
import { CLUSTER_REGISTRATION_CONNECTION_STATUS_MAP, statusMap, CONNECTION_STATUS } from '@/common/constants/clusterRegistration.constants';
import { TaskName } from '@/common/constants/taskNames.constants';

const mapper = (item: ClusterRegistration) => ({
  ...item,
  statusIcon: CLUSTER_REGISTRATION_CONNECTION_STATUS_MAP[item.connection_status].icon,
  statusText: CLUSTER_REGISTRATION_CONNECTION_STATUS_MAP[item.connection_status].text,
  isDeletable: !item.is_primary || (item.connection_status === CONNECTION_STATUS.FAILED || item.connection_status === CONNECTION_STATUS.REGISTRATION_FAILED || item.connection_status === CONNECTION_STATUS.PENDING)
});

export type ClusterRegistrationMapper = ReturnType<typeof mapper>;

export const useClusterRegistrationsStore = defineStore('clusterRegistrations', () => {
  const commonStore = useCommonStore<ClusterRegistration, ClusterRegistrationMapper>({
    getUrl: () => '/api/v2/cluster/registrations',
    plural: (data) => data,
    mapItem: mapper,
    dataChangeMode: 'task'
  })();

  const config = ref<ClusterRegistrationConfigShowResponse | null>(null);
  const testingConnections = ref<Record<ID, any>>({});

  const { changeItem, fetchLastItem, removeItem } = commonStore;

  const testConnection = (id: ID) => api.post<BackendTask>(`/api/v2/cluster/registrations/${id}/test-connection`);

  const fetchConfig = async () => {
    const response = await api.get<ClusterRegistrationConfigShowResponse>('/api/v2/cluster/registrations/config/');
    config.value = response.data;
    return response.data;
  };

  const updateConfig = async (payload: ClusterRegistrationConfigUpdate) => {
    await api.patch<ClusterRegistrationConfigShowResponse>('/api/v2/cluster/registrations/config/', payload);
    return fetchConfig();
  };

  const onTask = async (object: TaskObject): Promise<void> => {
    if (!object) return;

    const connectionStatus = statusMap[object.state];
    if (object.name === TaskName.CLUSTER_REGISTRATION_TEST_CONNECTION && connectionStatus) {
      if (object.state === TaskState.FAILED) {
        testingConnections.value[object.kwargs?.registration_uuid as ID] = object.error;
      } else {
        testingConnections.value[object.kwargs?.registration_uuid as ID] = null;
      }
    }

    if (object.name === TaskName.CLUSTER_REGISTRATION_DELETE && object.state === TaskState.RUNNING) {
      changeItem({ id: object.kwargs?.registration_uuid as ID, connection_status: CONNECTION_STATUS.CONFIGURING });
    }
    if (object.name === TaskName.CLUSTER_REGISTRATION_DELETE && object.state === TaskState.FAILED) {
      await fetchLastItem(object.kwargs?.registration_uuid as ID);
    }
    if (object.name === TaskName.CLUSTER_REGISTRATION_CREATE && object.state === TaskState.SUCCESS) {
      await fetchLastItem(object.result?.registration_id as ID);
    }
    if (object.name === TaskName.CLUSTER_REGISTRATION_DELETE && object.state === TaskState.SUCCESS) {
      removeItem(object.kwargs?.registration_uuid as ID);
    }
  };

  const onCluster = async (clusterEvent: SocketEvent): Promise<void> => {
    if (!clusterEvent) return;

    if (clusterEvent.type === 'registration' && (clusterEvent.operation === 'change' || clusterEvent.operation === 'updated')) {
      await fetchLastItem(clusterEvent.object.id as ID);
    }
  };

  return {
    ...commonStore,
    testingConnections,
    testConnection,
    fetchConfig,
    updateConfig,
    config,
    onTask,
    onCluster
  };
}, {
  ws: {
    tasks: 'onTask',
    cluster: 'onCluster'
  }
});

export default useClusterRegistrationsStore;
